import React from 'react';

interface CourseAnalyticsItem {
    courseId: number;
    courseName: string;
    completionPercentage: number;
    isCompleted: boolean;
    completionDate: string | null;
    totalMinutesSpent: number;
    practiceTestsAttempted: number;
    averageTestScore: number;
    bestTestScore: number;
    strongAreas: number;
    weakAreas: number;
    enrolledDate: string;
    lastAccessedDate: string;
}

interface CourseAnalyticsProps {
    courses: CourseAnalyticsItem[];
}

export default function CourseAnalytics({ courses }: CourseAnalyticsProps) {
    if (courses.length === 0) {
        return (
            <div className="bg-white rounded-lg shadow-sm p-8 text-center">
                <p className="text-gray-600">No courses found</p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {courses.map((course) => (
                <div key={course.courseId} className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition">
                    <div className="flex justify-between items-start mb-4">
                        <div>
                            <h3 className="text-xl font-bold text-gray-800">{course.courseName}</h3>
                            <p className="text-sm text-gray-500">Enrolled {new Date(course.enrolledDate).toLocaleDateString()}</p>
                        </div>
                        {course.isCompleted && (
                            <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                                Completed
                            </span>
                        )}
                    </div>

                    {/* Progress Bar */}
                    <div className="mb-4">
                        <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-medium text-gray-600">Progress</span>
                            <span className="text-sm font-bold text-indigo-600">{course.completionPercentage.toFixed(1)}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                                className="bg-indigo-600 h-2 rounded-full transition-all"
                                style={{ width: `${Math.min(100, course.completionPercentage)}%` }}
                            />
                        </div>
                    </div>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <StatItem
                            label="Study Time"
                            value={course.totalMinutesSpent}
                            unit="min"
                        />
                        <StatItem
                            label="Tests Attempted"
                            value={course.practiceTestsAttempted}
                        />
                        <StatItem
                            label="Average Score"
                            value={course.averageTestScore.toFixed(1)}
                            unit="%"
                        />
                        <StatItem
                            label="Best Score"
                            value={course.bestTestScore.toFixed(1)}
                            unit="%"
                        />
                    </div>
                </div>
            ))}
        </div>
    );
}

interface StatItemProps {
    label: string;
    value: number | string;
    unit?: string;
}

function StatItem({ label, value, unit }: StatItemProps) {
    return (
        <div className="p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-600 font-medium mb-1">{label}</p>
            <p className="text-lg font-bold text-gray-800">
                {value}{unit && <span className="text-sm ml-1">{unit}</span>}
            </p>
        </div>
    );
}
